
<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import client from '@js/api/client';


    const props = defineProps({
        endpoint: {
            type: String,
            required: true
        }
    });

    const localEntities = ref({});
    const loading = ref(false);
    const error = ref(null);

    const fetchEntities = async () => {
        if (!props.endpoint) {
            error.value = "Critical Error: 'endpoint' prop is missing.";
            return;
        }

        loading.value = true;
        error.value = null;

        try {
            const responseData = await client.get(props.endpoint);
            localEntities.value = responseData ? { ...responseData } : {};
        } catch (err) {
            error.value = err.response?.data?.message || 'Failed to load Doctrine mappings.';
            console.error('Fetch entities error:', err);
        } finally {
            loading.value = false;
        }
    };

    onMounted(() => {
        fetchEntities();
    });

    const scrollToEntity = (name) => {
        const el = document.getElementById(`entity-${name}`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
    };

    /**
     * Color codes badge types based on the data type
     */
    const getTypeClass = (type) => {
        const base = 'type-tag';

        switch (type) {
            case 'integer':
            case 'float':
            case 'decimal':
                return `${base} type-numeric`;

            case 'string':
            case 'text':
            case 'blob':
                return `${base} type-string`;

            case 'boolean':
                return `${base} type-boolean`;

            case 'datetime':
            case 'date':
                return `${base} type-date`;

            default:
                return `${base} type-default`;
        }
    };

    const searchQuery = ref('');
    const debouncedQuery = ref('');
    let debounceTimer = null;

    watch(searchQuery, (newValue) => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            debouncedQuery.value = newValue;
        }, 300);
    });

    const filteredEntities = computed(() => {
        const query = debouncedQuery.value.toLowerCase().trim();

        if (!query) return localEntities.value;

        return Object.entries(localEntities.value).reduce((result, [name, data]) => {
            // Check 1: Does the Entity Name match? (e.g., "Account")
            const nameMatch = name.toLowerCase().includes(query);

            // Check 2: Does the Table Name match? (e.g., "account_info")
            const tableMatch = data.table.toLowerCase().includes(query);

            // Check 3: Do any Field names match? (e.g., "shippingAccount")
            const fieldMatch = data.fields.some(f => f.name.toLowerCase().includes(query));

            // Check 4: Do any Association names match? (e.g., "orders")
            const assocMatch = data.associations.some(a => a.name.toLowerCase().includes(query));

            if (nameMatch || tableMatch || fieldMatch || assocMatch) {
                result[name] = data;
            }

            return result;
        }, {});
    });

    /**
     * Highlights the search query within a string
     * @param {string} text - The original text (e.g., "shippingAccount")
     * @param {string} query - The search term (e.g., "account")
     * @returns {string} - HTML string with <mark> tags
     */
    const highlight = (text, query) => {
        if (!query || !text) return text;

        // Escape special regex characters in the query
        const escapedQuery = query.replace(/[.*+?^${} ()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedQuery})`, 'gi');

        // Wrap matches in a Bootstrap-styled mark or custom span
        return text.toString().replace(regex, '<mark class="p-0 bg-highlight-yellow text-dark">$1</mark>');
    };
</script>


<template>
    <div>
        <div class="sticky-top bg-white border-bottom p-3 mb-4 shadow-sm" style="z-index: 1;">
            <div class="input-group">
                <span class="input-group-text bg-light border-end-0">
                    <i class="fas fa-search text-muted"></i>
                </span>
                <input
                    type="text"
                    v-model="searchQuery"
                    class="form-control border-start-0"
                    placeholder="Search entities, tables, or properties (e.g. 'zip' or 'order')..."
                >
                <button v-if="searchQuery" @click="searchQuery = ''" class="btn btn-outline-secondary">
                    Clear
                </button>
            </div>
            <div class="small text-muted mt-2" v-if="searchQuery">
                Showing {{ Object.keys(filteredEntities).length }} of {{ Object.keys(localEntities).length }} entities
            </div>
        </div>

        <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary"></div>
            <p>Parsing Doctrine Mappings...</p>
        </div>

        <div v-else-if="error" class="alert alert-danger">
            <strong>Error:</strong> {{ error }}
        </div>

        <div v-else-if="localEntities" class="row">
            <div class="col-md-3 border-end overflow-auto" style="max-height: 85vh;">
                <div class="list-group list-group-flush">
                    <button v-for="(entity, name) in filteredEntities"
                            :key="name + '-btn'"
                            @click="scrollToEntity(name)"
                            class="list-group-item list-group-item-action small"
                            v-html="highlight(name, debouncedQuery)">
                    </button>
                </div>
            </div>

            <div class="col-md-9 overflow-auto" style="max-height: 85vh;">
                <div v-for="(entity, name) in filteredEntities" :key="name" :id="'entity-' + name" class="card mb-5 shadow-sm">

                    <div class="card-header bg-white py-3">
                        <div class="d-flex justify-content-between align-items-center">
                            <h3 class="text-primary mb-0">{{ name }}</h3>
                            <span class="badge bg-primary">Table: <span v-html="highlight(entity.table, debouncedQuery)"></span></span>
                        </div>
                        <small class="text-muted text-monospace">{{ entity.namespace }}</small>
                    </div>

                    <div class="card-body">
                        <h6 class="text-uppercase text-muted fw-bold mb-3">Properties & Types</h6>
                        <table class="table table-sm table-hover border">
                            <thead class="table-light">
                            <tr>
                                <th>Property (Mapping)</th>
                                <th>DB Column</th>
                                <th>Type</th>
                                <th>Nullable</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="field in entity.fields" :key="field.name">
                                <td class="fw-bold" v-html="highlight(field.name, debouncedQuery)"></td>
                                <td><code class="text-muted small" v-html="highlight(field.db, debouncedQuery)"></code></td>
                                <td>
                                <span :class="getTypeClass(field.type)" class="badge">
                                    {{ field.type }}
                                </span>
                                </td>
                                <td class="">
                                    <i v-if="field.nullable" class="fa-solid fa-ghost text-muted" style="opacity: 0.5;" title="Nullable"></i>
<!--                                    <i v-else class="fa-solid fa-lock text-secondary" title="Required"></i>-->
                                </td>
                            </tr>
                            </tbody>
                        </table>

                        <h6 class="text-uppercase text-muted fw-bold mt-4 mb-3">Relationships (Associations)</h6>
                        <div class="row g-2">
                            <div v-for="rel in entity.associations" :key="rel.name" class="col-md-6 col-lg-4">
                                <div class="p-2 border rounded bg-light d-flex flex-column h-100">
                                    <span class="small text-muted" v-html="highlight(rel.type, debouncedQuery)"></span>
                                    <strong class="text-dark" v-html="highlight(rel.name, debouncedQuery)"></strong>
                                    <a href="javascript:void(0)"
                                       @click="scrollToEntity(rel.target)"
                                       class="mt-auto pt-1 small text-decoration-none">
                                        → <span v-html="highlight(rel.target, debouncedQuery)"></span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.pointer { cursor: pointer; }
.hover-bg-light:hover { background-color: #f8f9fa; }
.hover-shadow:hover { box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
/* Base style for all type tags */
.type-tag {
    display: inline-block;
    padding: 0.15rem 0.5rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.75rem;
    font-weight: 600;
    border-radius: 4px;
    text-transform: lowercase;
}

/* Numeric: Subdued Green */
.type-numeric {
    background-color: #ecfdf5;
    color: #065f46; /* Contrast 7.3:1 */
    border: 1px solid #d1fae5;
}

/* Text/Strings: Subdued Blue */
.type-string {
    background-color: #eff6ff;
    color: #1e40af; /* Contrast 6.8:1 */
    border: 1px solid #dbeafe;
}

/* Logic/Boolean: Subdued Amber */
.type-boolean {
    background-color: #fffbeb;
    color: #92400e; /* Contrast 5.1:1 */
    border: 1px solid #fef3c7;
}

/* Dates/Time: Subdued Indigo */
.type-date {
    background-color: #f5f3ff;
    color: #4338ca; /* Contrast 7.1:1 */
    border: 1px solid #ede9fe;
}

/* Default/Other: Neutral Gray */
.type-default {
    background-color: #f8fafc;
    color: #475569; /* Contrast 4.7:1 */
    border: 1px solid #e2e8f0;
}

</style>